import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query';

import { ApiError } from '@api/client';

const STALE_TIME = 30_000;
const MAX_RETRIES = 3;

export interface PaginatedListResponse {
  pagination: { page: number; per_page: number; total: number };
}

export interface ResourceListInfiniteQueryConfig<TResponse, TParams> {
  queryKey: (params: TParams) => readonly unknown[];
  fetcher: (params: TParams) => Promise<TResponse>;
  // Opt-in: holds previous pages across a key change. Safe only for a list
  // whose params are filters alone, never one keyed by a parent record.
  placeholderData?: (
    previous: InfiniteData<TResponse, number> | undefined
  ) => InfiniteData<TResponse, number> | undefined;
}

export interface UseResourceListInfiniteQueryOptions<TParams> {
  params?: TParams;
  enabled?: boolean;
}

export function createResourceListInfiniteQuery<
  TResponse extends PaginatedListResponse,
  TParams extends { page?: number },
>(config: ResourceListInfiniteQueryConfig<TResponse, TParams>) {
  return function useResourceListInfiniteQuery({
    params,
    enabled = true,
  }: UseResourceListInfiniteQueryOptions<TParams> = {}) {
    const resolvedParams = (params ?? {}) as TParams;
    return useInfiniteQuery({
      queryKey: config.queryKey(resolvedParams),
      queryFn: ({ pageParam }) =>
        config.fetcher({ ...resolvedParams, page: pageParam } as TParams),
      initialPageParam: 1,
      // Driven off `total`, not "last page was full" -- that heuristic fires
      // one wasted request when total is an exact multiple of per_page.
      getNextPageParam: (lastPage) => {
        const { page, per_page, total } = lastPage.pagination;
        return page * per_page < total ? page + 1 : undefined;
      },
      staleTime: STALE_TIME,
      placeholderData: config.placeholderData,
      enabled,
      retry: (failureCount, error) =>
        error instanceof ApiError && error.status >= 400 && error.status < 500
          ? false
          : failureCount < MAX_RETRIES,
    });
  };
}
